/**
 * Saved Colours — UI layer.
 *
 * Defines:
 *  - <colour-save-button>  the heart toggle rendered on every colour tile/PDP
 *  - <saved-colours-toast> a single, page-level "create an account" prompt
 *  - <saved-colours-page>  the grid/filter/sort/group-management controller
 *
 * All three are plain custom elements (Dawn's usual pattern), no build step.
 * Storage/merge/sync logic lives in the sibling saved-colours-*.js files;
 * this file only renders and wires up events.
 */
(function (global) {
  'use strict';

  var Storage = global.SavedColoursStorage;
  var CHANGE_EVENT = 'saved-colours:changed';
  var PROMPT_SHOWN_KEY = 'haymes:saved-colours:guest-prompt-shown';

  var DEFAULT_STRINGS = {
    saveColour: 'Save colour {name}',
    removeSavedColour: 'Remove saved colour {name}',
    savedAnnouncement: '{name} saved.',
    removedAnnouncement: '{name} removed.',
    unavailable: 'No longer available',
    guestPromptMessage: 'Keep your saved colours across devices and sessions.',
    guestPromptCreateAccount: 'Create Account',
    guestPromptDismiss: 'Dismiss message',
    manageMyColours: 'View saved colours & groups',
    limitWarning: 'You have saved {count} colours — consider removing some you no longer need.',
    colourRemoved: 'Colour removed from All Saved Colours.',
    groupDeleted: 'Group deleted.',
    groupDuplicateName: 'A group with that name already exists.',
    groupEmptyName: 'Please enter a group name.',
    addToProject: 'Save to group',
    addToProjectAvailable: 'Save to group available.',
    editGroups: 'Edit groups',
    doneEditingGroups: 'Done',
    groupsFor: 'Groups for {name}',
    chooseGroupsInstruction: 'Choose which groups to add this colour to.',
    defaultGroupTag: 'default',
    defaultGroupHint: 'All Saved Colours contains every colour you save. Add colours to specific groups below to organise your colours. Create and manage groups on the My Colours page.',
    groupAdded: '{name} added to {group}.',
    groupRemovedFrom: '{name} removed from {group}.',
    colourCountOne: '{count} colour',
    colourCountOther: '{count} colours',
    renameGroup: 'Rename {name}',
    deleteGroup: 'Delete {name}',
    deleteGroupConfirm: 'Delete {name}. Colours stay saved.',
    deleteGroupConfirmButton: 'Delete group',
    keepGroup: 'Keep',
    moveGroupUp: 'Move {name} up',
    moveGroupDown: 'Move {name} down',
    removeColourConfirm: 'This will remove the colour from all groups.',
    removeColourConfirmButton: 'Remove',
    cancelAction: 'Cancel',
  };

  function t(key, replacements) {
    var source = global.savedColoursStrings || {};
    var template = source[key] || DEFAULT_STRINGS[key] || '';
    return Object.keys(replacements || {}).reduce(function (str, token) {
      var pattern = new RegExp('\\[' + token + '\\]|\\{' + token + '\\}', 'g');
      return str.replace(pattern, replacements[token]);
    }, template);
  }

  function broadcastChange(state) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { state: state } }));
  }

  Storage.subscribe(broadcastChange);

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  var FALLBACK_SWATCH_COLOUR = '#d9d9d9';
  var HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
  var RGB_PATTERN = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\)$/i;

  // Guards against a bad/garbage metafield value silently rendering an
  // invisible (transparent) swatch — always falls back to a visible grey.
  function safeCssColour(value) {
    var trimmed = (value || '').trim();
    if (HEX_PATTERN.test(trimmed) || RGB_PATTERN.test(trimmed)) return trimmed;
    return FALLBACK_SWATCH_COLOUR;
  }

  // Assigns each group a consistent pastel colour for its tag pill (so
  // "Saved Colours" reads as the same colour everywhere, "Blue" as
  // another, etc.) without needing a colour field in the data model.
  // The default group is pinned to palette index 0; custom groups hash
  // their id into the remaining slots, in assets/saved-colours.css.
  var GROUP_TAG_COLOUR_COUNT = 6;

  function groupTagColourIndex(group) {
    if (group.isDefault) return 0;
    var sum = 0;
    for (var i = 0; i < group.id.length; i++) sum += group.id.charCodeAt(i);
    return 1 + (sum % (GROUP_TAG_COLOUR_COUNT - 1));
  }

  // Decorative paint-blob shape layered on top of the flat swatch colour.
  // Purely visual (the swatch's own background-colour + the colour name in
  // the tile body already carry the meaning), so it's alt="" and skipped
  // entirely if the config URL isn't available for any reason.
  var SWATCH_BLOB_SHAPE_URL = (global.savedColoursConfig && global.savedColoursConfig.swatchBlobShapeUrl) || '';

  function renderSwatchImage() {
    if (!SWATCH_BLOB_SHAPE_URL) return '';
    return (
      '<img class="saved-colours-tile__swatch-image" src="' + escapeHtml(SWATCH_BLOB_SHAPE_URL) + '" alt="" loading="lazy" width="200" height="200">'
    );
  }

  // Small inline icons for the group-row actions. Kept as plain strings
  // (no icon font/build step) so they inherit `color` via currentColor.
  var PENCIL_ICON =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M12 20h9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // Move-up / move-down controls for re-ordering custom groups — the
  // simplest reliable way to reorder that works identically with mouse,
  // touch, and keyboard, without needing HTML5 drag-and-drop (unsupported
  // on touch without extra polyfill code) or a drag library.
  var UP_ICON =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M5 15l7-7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var DOWN_ICON =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M5 9l7 7 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var TRASH_ICON =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M3 6h18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '<path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>';

  // Matches the theme's icon-caret.svg (used elsewhere on the page in the
  // filter/sort selects) so the "Edit groups" toggle uses the same chevron.
  var CARET_ICON =
    '<svg class="icon icon-caret" viewBox="0 0 10 6">' +
    '<path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path>' +
    '</svg>';

  var INFO_ICON =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>' +
    '<path d="M12 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '<circle cx="12" cy="7.5" r="1.1" fill="currentColor"/>' +
    '</svg>';

  // Same mark as the theme's icon-close.svg (used for the colour-planet
  // modal's close button) — a solid glyph reads more clearly at small sizes
  // than the thin "&times;" HTML entity this used to render.
  var CLOSE_ICON =
    '<svg width="12" height="12" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/>' +
    '</svg>';

  // Marks the default group's row in the group-picker checklist as
  // permanently "on" — it's a plain checkmark, not a checkbox, since that
  // row can never be unticked (see renderGroupCheckboxList below).
  var LOCKED_CHECK_ICON =
    '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // Info icon shown beside the default group's tag, replacing the old
  // standalone hint paragraph. The hint itself is announced via the
  // aria-label here; the visible tooltip bubble is a single shared
  // element appended to <body> (see showInfoTooltip() below) rather than
  // nested inside this markup — nesting it would get sliced off by
  // .saved-colours-tile's overflow: hidden (needed for its rounded
  // corners) whenever the tooltip is wider than the card.
  function renderInfoTooltip(hintText) {
    return (
      '<span class="saved-colours-group-picker__info" tabindex="0" data-sc-info-trigger aria-label="' + escapeHtml(hintText) + '">' +
      INFO_ICON +
      '</span>'
    );
  }

  // ---------------------------------------------------------------------
  // Floating tooltip — a single element reused for every info icon,
  // positioned in fixed (viewport) coordinates so it can never be
  // clipped by a scroll/overflow-hidden ancestor or trapped by a
  // transformed one (e.g. the tile's hover lift).
  // ---------------------------------------------------------------------
  var infoTooltipEl = null;
  function getInfoTooltipEl() {
    if (infoTooltipEl) return infoTooltipEl;
    infoTooltipEl = document.createElement('div');
    infoTooltipEl.className = 'saved-colours-floating-tooltip';
    infoTooltipEl.setAttribute('role', 'tooltip');
    infoTooltipEl.hidden = true;
    document.body.appendChild(infoTooltipEl);
    return infoTooltipEl;
  }

  function showInfoTooltip(trigger) {
    var tooltip = getInfoTooltipEl();
    tooltip.textContent = trigger.getAttribute('aria-label') || '';
    tooltip.hidden = false;

    var margin = 8;
    var triggerRect = trigger.getBoundingClientRect();
    var tooltipRect = tooltip.getBoundingClientRect();

    var left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - tooltipRect.width - margin));

    var top = triggerRect.top - tooltipRect.height - margin;
    var placedAbove = true;
    if (top < margin) {
      top = triggerRect.bottom + margin;
      placedAbove = false;
    }

    tooltip.style.left = left + 'px';
    tooltip.style.top = top + 'px';
    tooltip.classList.toggle('saved-colours-floating-tooltip--below', !placedAbove);
  }

  function hideInfoTooltip() {
    if (infoTooltipEl) infoTooltipEl.hidden = true;
  }

  // ---------------------------------------------------------------------
  // "Edit groups" popover — a single element reused for every tile, same
  // fixed-position-escapes-clipping approach as the tooltip above. Opens
  // below its trigger by default (like a normal dropdown); flips above
  // when there isn't room underneath, the same way showInfoTooltip() does.
  // ---------------------------------------------------------------------
  var GROUP_EDITOR_POPOVER_ID = 'SavedColoursGroupEditorPopover';
  var groupEditorPopoverEl = null;
  function getGroupEditorPopoverEl() {
    if (groupEditorPopoverEl) return groupEditorPopoverEl;
    groupEditorPopoverEl = document.createElement('div');
    groupEditorPopoverEl.className = 'saved-colours-tile__group-editor';
    groupEditorPopoverEl.id = GROUP_EDITOR_POPOVER_ID;
    groupEditorPopoverEl.hidden = true;
    document.body.appendChild(groupEditorPopoverEl);
    return groupEditorPopoverEl;
  }

  function positionGroupEditorPopover(popover, trigger) {
    var margin = 8;
    var triggerRect = trigger.getBoundingClientRect();
    var popoverRect = popover.getBoundingClientRect();

    // Centered on the trigger's own midpoint rather than left-aligned to
    // it — with the popover much wider than the "Edit groups" link that
    // opens it, left-aligning made it read as jutting off to one side
    // instead of belonging to the control underneath it.
    var left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - popoverRect.width - margin));

    var top = triggerRect.bottom + margin;
    if (top + popoverRect.height > window.innerHeight - margin) {
      top = triggerRect.top - popoverRect.height - margin;
    }
    top = Math.max(margin, top);

    popover.style.left = left + 'px';
    popover.style.top = top + 'px';
  }

  function hideGroupEditorPopover() {
    if (groupEditorPopoverEl) groupEditorPopoverEl.hidden = true;
  }

  function initInfoTooltips() {
    document.addEventListener('focusin', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-sc-info-trigger]');
      if (trigger) showInfoTooltip(trigger);
    });

    document.addEventListener('focusout', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-sc-info-trigger]');
      if (trigger) hideInfoTooltip();
    });

    document.addEventListener('mouseover', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-sc-info-trigger]');
      if (trigger) showInfoTooltip(trigger);
    });

    document.addEventListener('mouseout', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-sc-info-trigger]');
      var related = event.relatedTarget;
      if (trigger && !(related && trigger.contains(related))) hideInfoTooltip();
    });

    // Re-run positioning on scroll (fixed coordinates go stale otherwise);
    // simplest correct behaviour is to just hide it, matching how native
    // title tooltips behave when the page scrolls under the cursor.
    window.addEventListener('scroll', hideInfoTooltip, { passive: true });
  }

  // ---------------------------------------------------------------------
  // Shared group-picker checklist — used by both the post-save toast's
  // "Add to project" panel and the Saved Colours page's "Edit groups"
  // panel. A colour can belong to any number of groups, including zero;
  // unchecking "Saved Colours" is a valid, explicit action.
  // ---------------------------------------------------------------------
  function renderGroupCheckboxList(idPrefix, productId, groups, colourGroupIds) {
    return groups.map(function (group) {
      // The default group ("All Saved Colours") is the master list every
      // saved colour belongs to automatically — it has no checkbox at all
      // (rather than a checked-but-disabled one) so there's nothing here a
      // customer could mistake for an untickable membership control.
      if (group.isDefault) {
        return (
          '<li class="saved-colours-group-picker__item saved-colours-group-picker__item--locked">' +
          '<span class="saved-colours-group-picker__locked-check" aria-hidden="true">' + LOCKED_CHECK_ICON + '</span>' +
          '<span class="saved-colours-group-picker__locked-label">' +
          escapeHtml(group.name) +
          ' <span class="saved-colours-group-picker__default-tag">' + escapeHtml(t('defaultGroupTag')) + '</span>' +
          '</span>' +
          renderInfoTooltip(t('defaultGroupHint')) +
          '</li>'
        );
      }

      var checkboxId = idPrefix + '-' + productId + '-' + group.id;
      var checked = colourGroupIds.indexOf(group.id) > -1;
      return (
        '<li class="saved-colours-group-picker__item">' +
        '<input type="checkbox" class="saved-colours-group-picker__checkbox" id="' + escapeHtml(checkboxId) + '"' +
        ' data-sc-group-checkbox data-product-id="' + escapeHtml(productId) + '" data-group-id="' + escapeHtml(group.id) + '"' +
        (checked ? ' checked' : '') + '>' +
        '<label for="' + escapeHtml(checkboxId) + '">' +
        escapeHtml(group.name) +
        '</label>' +
        '</li>'
      );
    }).join('');
  }

  // Delegated handler shared by the toast's picker and the page's per-tile
  // picker: reads the checkbox's own data attributes, so it doesn't matter
  // which container it lives in.
  function handleGroupCheckboxChange(event) {
    var checkbox = event.target.closest && event.target.closest('[data-sc-group-checkbox]');
    if (!checkbox) return;

    var productId = checkbox.dataset.productId;
    var groupId = checkbox.dataset.groupId;
    var state = Storage.getState();
    var group = state.groups.find(function (g) { return g.id === groupId; });
    var colour = state.colours.find(function (c) { return c.productId === productId; });
    var groupName = group ? group.name : '';
    var colourName = colour ? colour.name : '';

    if (checkbox.checked) {
      Storage.assignToGroup(productId, groupId);
      getLiveRegion().textContent = t('groupAdded', { name: colourName, group: groupName });
    } else {
      Storage.removeFromGroup(productId, groupId);
      getLiveRegion().textContent = t('groupRemovedFrom', { name: colourName, group: groupName });
    }
  }

  // ---------------------------------------------------------------------
  // <colour-save-button>
  // ---------------------------------------------------------------------
  function defineSaveButton() {
    class SavedColourButtonElement extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.colour-save-button__control');
        if (!this.button || this.dataset.scReady) return;
        this.dataset.scReady = 'true';

        this.productId = String(this.dataset.productId || '');
        this.colour = {
          productId: this.productId,
          handle: this.dataset.productHandle || '',
          name: this.dataset.productName || '',
          hex: this.dataset.productHex || '',
        };
        this._lastTouchHandledAt = 0;

        this._onClick = this.handleClick.bind(this);
        this.button.addEventListener('click', this._onClick);

        // Colour cards reveal their Order Sample/Order Paint/Shop Now
        // buttons via a plain CSS :hover rule on an ancestor
        // (assets/buttons-layer.css). On touch devices, tapping anywhere
        // in that ancestor — including this button — triggers that
        // :hover state as a side effect, and mobile browsers then require
        // a *second* tap to actually deliver the click (the first tap is
        // treated as "reveal the hover state, let the user confirm").
        // Handling touchend directly, with preventDefault, fires the
        // toggle immediately on the first tap and suppresses the
        // follow-up synthetic click the browser would otherwise send.
        this._onTouchEnd = this.handleTouchEnd.bind(this);
        this.button.addEventListener('touchend', this._onTouchEnd);

        this._onChange = this.syncPressedState.bind(this);
        document.addEventListener(CHANGE_EVENT, this._onChange);

        this.syncPressedState();
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener('click', this._onClick);
          this.button.removeEventListener('touchend', this._onTouchEnd);
        }
        document.removeEventListener(CHANGE_EVENT, this._onChange);
      }

      syncPressedState() {
        if (!this.productId) return;
        var saved = Storage.isColourSaved(this.productId);
        this.setPressed(saved);
      }

      setPressed(saved) {
        var name = this.colour.name || 'colour';
        this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        this.button.setAttribute('aria-label', t(saved ? 'removeSavedColour' : 'saveColour', { name: name }));
        this.classList.toggle('is-saved', saved);
      }

      handleTouchEnd(event) {
        event.preventDefault();
        this._lastTouchHandledAt = Date.now();
        hideInfoTooltip();
        this.toggleSavedColour();
      }

      handleClick(event) {
        event.preventDefault();
        event.stopPropagation();
        // A touchend just handled this exact tap — skip the browser's
        // follow-up synthetic click, or this would immediately toggle
        // the colour straight back off again.
        if (Date.now() - this._lastTouchHandledAt < 500) return;
        this.toggleSavedColour();
      }

      toggleSavedColour() {
        if (!this.productId) return;

        var result = Storage.toggleColour(this.colour);
        this.setPressed(result.saved);
        var name = this.colour.name || 'Colour';

        // showSaveConfirmation announces the result via the shared
        // aria-live region itself (and mentions "Add to project" there
        // when relevant), so there's no separate announce call here.
        showSaveConfirmation(this.button, this.productId, name, result.saved);
        if (result.saved) maybeWarnStorageLimit();
      }
    }

    customElements.define('colour-save-button', SavedColourButtonElement);
  }

  var liveRegionEl = null;
  function getLiveRegion() {
    if (liveRegionEl) return liveRegionEl;
    liveRegionEl = document.createElement('div');
    liveRegionEl.setAttribute('aria-live', 'polite');
    liveRegionEl.setAttribute('role', 'status');
    liveRegionEl.className = 'visually-hidden saved-colours-live-region';
    document.body.appendChild(liveRegionEl);
    return liveRegionEl;
  }

  // ---------------------------------------------------------------------
  // Save/remove confirmation toast (every action) + one-time guest
  // "create an account" nudge, folded into that same toast the first
  // time a guest saves a colour.
  // ---------------------------------------------------------------------
  function isGuest() {
    return !(global.customerConfig && global.customerConfig.isLoggedIn);
  }

  function hasShownGuestPrompt() {
    try {
      return !!window.localStorage.getItem(PROMPT_SHOWN_KEY);
    } catch (error) {
      return false;
    }
  }

  function markGuestPromptShown() {
    try {
      window.localStorage.setItem(PROMPT_SHOWN_KEY, '1');
    } catch (error) {
      /* if we can't remember it was shown, worst case it shows again */
    }
  }

  function showSaveConfirmation(triggerEl, productId, name, saved) {
    var heading = t(saved ? 'savedAnnouncement' : 'removedAnnouncement', { name: name });
    var state = Storage.getState();
    var customGroups = state.groups.filter(function (g) { return !g.isDefault; });
    // Only offer "Save to group" once the customer has at least one group
    // beyond the default — nothing to add to otherwise, and never on a
    // remove action. Pass every group (not just the custom ones) so the
    // checklist can show the locked "All Saved Colours" master row above
    // the customer's own groups.
    var groupPicker = saved && customGroups.length > 0
      ? { productId: productId, groups: state.groups }
      : null;

    // A guest's very first save is a teaching moment for the account
    // prompt specifically — just the heading, the "keep your colours"
    // subtext, and a Create Account button, rather than competing with
    // "Save to group"/"View all saved colours" for the same few seconds
    // of attention.
    if (saved && isGuest() && !hasShownGuestPrompt()) {
      markGuestPromptShown();
      getLiveRegion().textContent = heading + ' ' + t('guestPromptMessage');
      getToast().show(triggerEl, {
        heading: heading,
        subtext: t('guestPromptMessage'),
        accountCta: true,
        showManageLink: false,
        autoDismiss: false,
        groupPicker: null,
      });
      return;
    }

    getLiveRegion().textContent = groupPicker ? heading + ' ' + t('addToProjectAvailable') : heading;
    getToast().show(triggerEl, {
      heading: heading,
      autoDismiss: true,
      groupPicker: groupPicker,
    });
  }

  function maybeWarnStorageLimit() {
    var state = Storage.getState();
    if (state.colours.length === Storage.MAX_RECOMMENDED_COLOURS) {
      getToast().show(null, {
        heading: t('limitWarning', { count: Storage.MAX_RECOMMENDED_COLOURS }),
        showManageLink: false,
        autoDismiss: false,
      });
    }
  }

  var toastInstance = null;
  function getToast() {
    if (toastInstance) return toastInstance;

    var el = document.createElement('saved-colours-toast');
    document.body.appendChild(el);
    toastInstance = el;
    return el;
  }

  var TOAST_AUTO_DISMISS_MS = 4000;

  function defineToast() {
    class SavedColoursToast extends HTMLElement {
      connectedCallback() {
        // Deliberately not a live region itself — the shared hidden
        // getLiveRegion() is the single source of truth for announcements,
        // so screen reader users don't hear everything twice. This element
        // is a purely visual toast; its interactive controls (dismiss,
        // create-account link, Add to project) are still reachable by Tab.
        this.hidden = true;
        this._lastFocused = null;
        this._autoDismissTimer = null;

        this.addEventListener('change', handleGroupCheckboxChange);
      }

      // options: { heading, subtext, accountCta, groupPicker, showManageLink,
      // autoDismiss }. Three call shapes cover the whole feature:
      //  - guest's first save:  heading + subtext + "Create Account" CTA
      //  - has custom groups:   heading + "Save to group" CTA (+ checklist)
      //  - no custom groups:    heading + "View all saved colours" link only
      show(triggerEl, options) {
        options = options || {};
        this._lastFocused = triggerEl || document.activeElement;
        clearTimeout(this._autoDismissTimer);

        var ctaHtml = '';
        if (options.accountCta) {
          ctaHtml = '<a class="saved-colours-toast__cta" href="/pages/sign-up-for-a-haymes-paint-account">' + escapeHtml(t('guestPromptCreateAccount')) + '</a>';
        } else if (options.groupPicker) {
          var picker = options.groupPicker;
          var colour = Storage.getColour(picker.productId);
          var colourGroupIds = colour ? colour.groupIds : [];
          ctaHtml =
            '<button type="button" class="saved-colours-toast__cta" data-sc-toggle-group-picker aria-expanded="false">' +
            escapeHtml(t('addToProject')) +
            '</button>' +
            '<fieldset class="saved-colours-toast__group-picker" data-sc-group-picker hidden>' +
            '<ul class="saved-colours-group-picker">' +
            renderGroupCheckboxList('toast-group', picker.productId, picker.groups, colourGroupIds) +
            '</ul>' +
            '</fieldset>';
        }

        this.innerHTML =
          '<div class="saved-colours-toast__inner">' +
          '<button type="button" class="saved-colours-toast__close" aria-label="' + escapeHtml(t('guestPromptDismiss')) + '">' +
          '<span aria-hidden="true">&times;</span>' +
          '</button>' +
          '<div class="saved-colours-toast__row">' +
          '<span class="saved-colours-toast__icon" aria-hidden="true">' + LOCKED_CHECK_ICON + '</span>' +
          '<div class="saved-colours-toast__content">' +
          '<p class="saved-colours-toast__heading">' + escapeHtml(options.heading || '') + '</p>' +
          (options.subtext ? '<p class="saved-colours-toast__subtext">' + escapeHtml(options.subtext) + '</p>' : '') +
          ctaHtml +
          (options.showManageLink === false
            ? ''
            : '<a class="saved-colours-toast__manage-link" href="/pages/my-colours">' + escapeHtml(t('manageMyColours')) + ' <span aria-hidden="true">&rarr;</span></a>') +
          '</div>' +
          '</div>' +
          '</div>';

        this.hidden = false;
        this.classList.add('is-visible');

        var closeBtn = this.querySelector('.saved-colours-toast__close');
        closeBtn.addEventListener('click', function () { this.dismiss(true); }.bind(this));

        var toggleBtn = this.querySelector('[data-sc-toggle-group-picker]');
        if (toggleBtn) {
          var panel = this.querySelector('[data-sc-group-picker]');
          toggleBtn.addEventListener('click', function () {
            var expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
            toggleBtn.setAttribute('aria-expanded', String(!expanded));
            panel.hidden = expanded;
            // Once the customer starts choosing groups, don't pull the
            // toast out from under them — they dismiss it manually instead.
            if (!expanded) clearTimeout(this._autoDismissTimer);
          }.bind(this));

          // Clicking elsewhere in the toast (the heading, the icon, blank
          // padding) is still "inside" the toast as far as the outside-click
          // handler above is concerned, so it wouldn't otherwise collapse
          // this checklist — it needs its own listener so a click anywhere
          // outside the checklist itself closes just the checklist, whether
          // that click lands outside the toast entirely or elsewhere within it.
          document.removeEventListener('click', this._onGroupPickerOutsideClick);
          this._onGroupPickerOutsideClick = function (event) {
            if (toggleBtn.getAttribute('aria-expanded') !== 'true') return;
            if (panel.contains(event.target) || toggleBtn.contains(event.target)) return;
            toggleBtn.setAttribute('aria-expanded', 'false');
            panel.hidden = true;
          };
          setTimeout(function () {
            document.addEventListener('click', this._onGroupPickerOutsideClick);
          }.bind(this), 0);
        }

        if (options.autoDismiss !== false) {
          this._autoDismissTimer = setTimeout(function () {
            this.dismiss(false);
          }.bind(this), TOAST_AUTO_DISMISS_MS);
        }

        // Click-outside-to-close. Attached on the next tick, not
        // synchronously — the click that just opened this toast (e.g. the
        // heart button) is still bubbling up to `document` when show()
        // runs, and a listener added now would catch that same click and
        // close the toast immediately after it appeared.
        document.removeEventListener('click', this._onOutsideClick);
        this._onOutsideClick = function (event) {
          if (this.contains(event.target)) return;
          this.dismiss(false);
        }.bind(this);
        setTimeout(function () {
          document.addEventListener('click', this._onOutsideClick);
        }.bind(this), 0);
      }

      dismiss(restoreFocus) {
        clearTimeout(this._autoDismissTimer);
        document.removeEventListener('click', this._onOutsideClick);
        document.removeEventListener('click', this._onGroupPickerOutsideClick);
        this.hidden = true;
        this.classList.remove('is-visible');
        if (restoreFocus && this._lastFocused && typeof this._lastFocused.focus === 'function') {
          this._lastFocused.focus();
        }
      }
    }

    customElements.define('saved-colours-toast', SavedColoursToast);
  }

  // ---------------------------------------------------------------------
  // <saved-colours-page>
  // ---------------------------------------------------------------------
  function defineSavedColoursPage() {
    class SavedColoursPageElement extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-sc-grid]');
        this.emptyState = this.querySelector('[data-sc-empty]');
        this.skeleton = this.querySelector('[data-sc-skeleton]');
        this.groupFilter = this.querySelector('[data-sc-group-filter]');
        this.sortSelect = this.querySelector('[data-sc-sort]');
        this.groupManager = this.querySelector('[data-sc-group-manager]');
        this.limitWarning = this.querySelector('[data-sc-limit-warning]');
        this.addGroupForm = this.querySelector('[data-sc-add-group-form]');
        this.addGroupError = this.querySelector('[data-sc-add-group-error]');
        this.countEl = this.querySelector('[data-sc-count]');

        this.sort = 'recent';
        this.activeGroup = 'all';
        this.openGroupEditorProductId = null;
        // Which single group row (if any) is currently showing its inline
        // rename input, or its inline "delete this group?" confirmation.
        // Only one of these — and only one row at a time — can be open.
        this.renamingGroupId = null;
        this.deletingGroupId = null;
        // productId of a tile whose "x" was clicked while its colour is in
        // more than one custom group — shows an inline "remove from all
        // groups?" confirmation on that tile instead of deleting right away.
        this.confirmingRemoveProductId = null;
        // { groupId, message } for a rename that was just rejected (e.g.
        // duplicate name) — shown inline under that row's rename input
        // until the user edits it or cancels/confirms again.
        this.renameGroupError = null;

        this._onChange = this.render.bind(this);
        document.addEventListener(CHANGE_EVENT, this._onChange);

        if (this.groupFilter) {
          this.groupFilter.addEventListener('change', function (event) {
            this.activeGroup = event.target.value;
            // A pending "remove from all groups?" confirmation belongs to
            // the view it was opened in — carrying it over to a different
            // group filter would leave that tile stuck showing the
            // overlay for an action the customer never asked for here.
            this.confirmingRemoveProductId = null;
            this.render();
            this.scrollGridIntoView();
          }.bind(this));
        }

        if (this.sortSelect) {
          this.sortSelect.addEventListener('change', function (event) {
            this.sort = event.target.value;
            this.render();
            this.scrollGridIntoView();
          }.bind(this));
        }

        if (this.addGroupForm) {
          this.addGroupForm.addEventListener('submit', this.handleAddGroup.bind(this));
          var groupNameInput = this.addGroupForm.querySelector('input[name="group-name"]');
          if (groupNameInput) {
            groupNameInput.addEventListener('input', this.clearAddGroupError.bind(this));
          }
        }

        if (this.grid) {
          this.grid.addEventListener('click', this.handleGridClick.bind(this));
          this.grid.addEventListener('change', handleGroupCheckboxChange);
        }
        if (this.groupManager) {
          this.groupManager.addEventListener('click', this.handleGroupManagerClick.bind(this));
          this.groupManager.addEventListener('keydown', this.handleGroupManagerKeydown.bind(this));
          this.groupManager.addEventListener('input', this.handleGroupManagerInput.bind(this));
        }

        // The "Edit groups" popover lives outside .saved-colours-grid (see
        // getGroupEditorPopoverEl()), so it needs its own listeners rather
        // than falling under the grid's delegated ones above.
        this.groupEditorPopover = getGroupEditorPopoverEl();
        this.groupEditorPopover.addEventListener('change', handleGroupCheckboxChange);
        this.groupEditorPopover.addEventListener('click', function (event) {
          if (event.target.closest('[data-sc-close-group-editor]')) {
            this.closeGroupEditorPopover();
          }
        }.bind(this));

        this._onOutsideClick = function (event) {
          if (!this.openGroupEditorProductId) return;
          var withinPopover = this.groupEditorPopover.contains(event.target);
          var withinTrigger = event.target.closest && event.target.closest('[data-sc-edit-groups]');
          if (!withinPopover && !withinTrigger) this.closeGroupEditorPopover(false);
        }.bind(this);
        document.addEventListener('click', this._onOutsideClick);

        this._onEscapeKey = function (event) {
          if (event.key === 'Escape' && this.openGroupEditorProductId) this.closeGroupEditorPopover();
        }.bind(this);
        document.addEventListener('keydown', this._onEscapeKey);

        // Fixed-position coordinates go stale once the trigger scrolls out
        // from under them — same reasoning as hideInfoTooltip() on scroll.
        this._onScroll = function () {
          if (this.openGroupEditorProductId) this.closeGroupEditorPopover(false);
        }.bind(this);
        window.addEventListener('scroll', this._onScroll, { passive: true });

        this.showSkeleton();

        var syncStatusUnsub = global.SavedColoursSync
          ? global.SavedColoursSync.onStatusChange(function (status) {
              if (status !== 'pulling') {
                this.hideSkeleton();
                this.render();
              }
            }.bind(this))
          : null; 
        this._syncStatusUnsub = syncStatusUnsub;

        if (!global.SavedColoursSync || global.SavedColoursSync.getStatus() !== 'pulling') {
          this.hideSkeleton();
        }

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(CHANGE_EVENT, this._onChange);
        document.removeEventListener('click', this._onOutsideClick);
        document.removeEventListener('keydown', this._onEscapeKey);
        window.removeEventListener('scroll', this._onScroll);
        if (this._syncStatusUnsub) this._syncStatusUnsub();
      }

      showSkeleton() {
        if (this.skeleton) this.skeleton.hidden = false;
        if (this.grid) this.grid.hidden = true;
      }

      hideSkeleton() {
        if (this.skeleton) this.skeleton.hidden = true;
        if (this.grid) this.grid.hidden = false;
      }

      getVisibleColours(state) {
        var colours = state.colours.slice();

        if (this.activeGroup !== 'all') {
          colours = colours.filter(function (c) { return c.groupIds.indexOf(this.activeGroup) > -1; }.bind(this));
        }

        if (this.sort === 'alphabetical') {
          colours.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
        } else {
          colours.sort(function (a, b) { return new Date(b.savedAt) - new Date(a.savedAt); });
        }

        return colours;
      }

      render() {
        var state = Storage.getState();

        // A sync merge can dedupe/remap group ids (e.g. two devices both
        // created a group with the same name). If the group this device
        // had selected as a filter no longer exists post-merge, fall back
        // to "all" — otherwise the dropdown would reset to "All Saved
        // Colours" visually (renderGroupFilter already guards that) while
        // the grid kept silently filtering on the stale, now-nonexistent id.
        if (this.activeGroup !== 'all' && !state.groups.some(function (g) { return g.id === this.activeGroup; }, this)) {
          this.activeGroup = 'all';
        }

        this.renderGroupFilter(state);
        this.renderGroupManager(state);

        if (this.limitWarning) {
          var near = state.colours.length >= Storage.MAX_RECOMMENDED_COLOURS;
          this.limitWarning.hidden = !near;
        }

        var visible = this.getVisibleColours(state);

        if (this.countEl) {
          this.countEl.textContent = visible.length === 1
            ? t('colourCountOne', { count: visible.length })
            : t('colourCountOther', { count: visible.length });
        }

        if (this.emptyState) {
          this.emptyState.hidden = visible.length > 0;
        }
        if (!this.grid) return;

        // Rebuilding the grid's innerHTML (e.g. after a group checkbox
        // toggle) would otherwise drop keyboard focus back to <body> —
        // ids are deterministic, so re-find and refocus the same element.
        var activeId = document.activeElement && document.activeElement.id;

        this.grid.innerHTML = visible.map(function (colour) {
          return this.renderTile(colour, state);
        }.bind(this)).join('');

        // The grid rebuild above just destroyed and recreated every tile,
        // including whichever "Edit groups" trigger the popover was
        // anchored to — re-show (with fresh content) against the new one,
        // or hide it if nothing's open. Must happen before the refocus
        // below: showGroupEditorPopover() rebuilds the popover's own
        // checkbox markup too, so a checkbox that was focused when this
        // render() started (e.g. the one the user just toggled) only
        // exists again, at the same id, once this has run.
        if (this.openGroupEditorProductId) {
          this.showGroupEditorPopover(this.openGroupEditorProductId);
        } else {
          hideGroupEditorPopover();
        }

        if (activeId) {
          var toRefocus = document.getElementById(activeId);
          if (toRefocus) toRefocus.focus();
        }
      }

      showGroupEditorPopover(productId) {
        var state = Storage.getState();
        var colour = state.colours.find(function (c) { return c.productId === productId; });
        var trigger = document.getElementById('edit-groups-' + productId);
        if (!colour || !trigger) {
          this.openGroupEditorProductId = null;
          hideGroupEditorPopover();
          return;
        }

        var popover = this.groupEditorPopover;
        popover.innerHTML =
          '<fieldset>' +
          '<legend class="visually-hidden">' + escapeHtml(t('groupsFor', { name: colour.name })) + '</legend>' +
          '<ul class="saved-colours-group-picker">' +
          renderGroupCheckboxList('tile-group', colour.productId, state.groups, colour.groupIds) +
          '</ul>' +
          '</fieldset>' +
          '<button type="button" class="saved-colours-inline-action" data-sc-close-group-editor>' +
          escapeHtml(t('doneEditingGroups')) +
          '</button>';
        popover.hidden = false;
        positionGroupEditorPopover(popover, trigger);
      }

      closeGroupEditorPopover(focusTrigger) {
        var productId = this.openGroupEditorProductId;
        this.openGroupEditorProductId = null;
        this.render();
        if (focusTrigger !== false && productId) {
          var trigger = document.getElementById('edit-groups-' + productId);
          if (trigger) trigger.focus();
        }
      }

      renderTile(colour, state) {
        // Every saved colour lives in the default group automatically, so
        // showing it as a tag alongside the customer's own groups is just
        // noise — only their explicit custom groups are worth a tag here.
        var tileGroups = colour.groupIds
          .map(function (id) {
            var group = state.groups.find(function (g) { return g.id === id; });
            return group && !group.isDefault ? { name: group.name, colourIndex: groupTagColourIndex(group) } : null;
          })
          .filter(Boolean);

        var url = colour.handle ? '/products/' + colour.handle : null;
        var swatchColour = safeCssColour(colour.hex);
        var isEditorOpen = this.openGroupEditorProductId === colour.productId;
        var editGroupsId = 'edit-groups-' + colour.productId;

        return (
          '<li class="saved-colours-tile" data-sc-tile data-product-id="' + escapeHtml(colour.productId) + '">' +
          '<div class="saved-colours-tile__swatch" style="background-color: ' + escapeHtml(swatchColour) + ';">' +
          renderSwatchImage() +
          '</div>' +
          '<div class="saved-colours-tile__body">' +
          (url
            ? '<a class="saved-colours-tile__name" href="' + escapeHtml(url) + '" data-sc-availability-link>' + escapeHtml(colour.name) + '</a>'
            : '<span class="saved-colours-tile__name">' + escapeHtml(colour.name) + '</span>') +
          '<p class="saved-colours-tile__unavailable" data-sc-unavailable hidden>' + escapeHtml(t('unavailable')) + '</p>' +
          (tileGroups.length
            ? '<ul class="saved-colours-tile__groups">' +
              tileGroups.map(function (g) {
                return '<li class="saved-colours-tile__group-tag saved-colours-tile__group-tag--' + g.colourIndex + '">' + escapeHtml(g.name) + '</li>';
              }).join('') +
              '</ul>'
            : '') +
          '<button type="button" class="saved-colours-inline-action" id="' + editGroupsId + '" data-sc-edit-groups ' +
          'aria-expanded="' + (isEditorOpen ? 'true' : 'false') + '" aria-controls="' + GROUP_EDITOR_POPOVER_ID + '" ' +
          'aria-label="' + escapeHtml(t('groupsFor', { name: colour.name })) + '">' +
          escapeHtml(t('editGroups')) +
          '<span class="svg-wrapper">' + CARET_ICON + '</span>' +
          '</button>' +
          '</div>' +
          '<button type="button" class="saved-colours-tile__remove" data-sc-remove aria-label="' + escapeHtml(t('removeSavedColour', { name: colour.name })) + '">' +
          CLOSE_ICON +
          '</button>' +
          (this.confirmingRemoveProductId === colour.productId ? this.renderRemoveConfirm(colour) : '') +
          '</li>'
        );
      }

      // Shown over a tile instead of removing it immediately, when that
      // colour sits in more than one custom group — deleting it here would
      // otherwise silently drop it out of every one of those groups at
      // once. Plain text, no icon, matching the existing inline
      // "Delete group?" confirmation style used in the group manager.
      renderRemoveConfirm(colour) {
        return (
          '<div class="saved-colours-tile__confirm-overlay" data-sc-remove-confirm>' +
          '<p class="saved-colours-tile__confirm-text">' + escapeHtml(t('removeColourConfirm')) + '</p>' +
          '<span class="saved-colours-tile__confirm-actions">' +
          '<button type="button" class="saved-colours-group-row__action-btn saved-colours-group-row__action-btn--danger" data-sc-confirm-remove>' + escapeHtml(t('removeColourConfirmButton')) + '</button>' +
          '<button type="button" class="saved-colours-group-row__action-btn" data-sc-cancel-remove>' + escapeHtml(t('cancelAction')) + '</button>' +
          '</span>' +
          '</div>'
        );
      }

      renderGroupFilter(state) {
        if (!this.groupFilter) return;
        var current = this.activeGroup;
        // The default group's own id must never appear as an option value
        // here — every other view of "everything" (the left panel's
        // select-group handler, the grid filter) uses the 'all' sentinel,
        // not the default group's real id. A second, distinct
        // "All Saved Colours" option keyed by its real id used to exist
        // alongside this one, which meant picking it set activeGroup to
        // that id instead of 'all' and the left panel's row never matched.
        var optionValues = state.groups.map(function (g) { return g.isDefault ? 'all' : g.id; });
        this.groupFilter.innerHTML = state.groups.map(function (g, index) {
          return '<option value="' + escapeHtml(optionValues[index]) + '">' + escapeHtml(g.name) + '</option>';
        }).join('');
        this.groupFilter.value = optionValues.indexOf(current) !== -1 ? current : 'all';
      }

      // Every non-default row renders one of three mutually-exclusive
      // states: idle (select button + move/pencil/trash icon buttons),
      // renaming (inline input + Save/Cancel), or confirming delete (inline
      // warning text + Delete group/Keep). The default "All Saved Colours"
      // row is selectable (it filters the grid down to "all", since every
      // saved colour lives there) but never gets rename/delete/move
      // controls — it's the fixed master list every other group sits on
      // top of, not a group a customer manages themselves.
      renderGroupManager(state) {
        if (!this.groupManager) return;
        var list = this.groupManager.querySelector('[data-sc-group-list]');
        if (!list) return;

        var customGroups = state.groups.filter(function (g) { return !g.isDefault; });

        list.innerHTML = state.groups.map(function (g) {
          var isSelected = g.isDefault ? this.activeGroup === 'all' : this.activeGroup === g.id;

          if (g.isDefault) {
            return (
              '<li class="saved-colours-group-row saved-colours-group-row--default' + (isSelected ? ' is-selected' : '') + '" data-group-id="' + escapeHtml(g.id) + '">' +
              '<button type="button" class="saved-colours-group-row__select" data-sc-select-group aria-pressed="' + (isSelected ? 'true' : 'false') + '">' +
              '<span class="saved-colours-group-row__name">' +
              escapeHtml(g.name) +
              '<span class="saved-colours-group-row__tag">' + escapeHtml(t('defaultGroupTag')) + '</span>' +
              '</span>' +
              '</button>' +
              '</li>'
            );
          }

          var isRenaming = this.renamingGroupId === g.id;
          var isDeleting = this.deletingGroupId === g.id;
          var renameInputId = 'saved-colours-rename-' + g.id;

          var renameError = this.renameGroupError && this.renameGroupError.groupId === g.id ? this.renameGroupError.message : null;

          var mainContent;
          if (isRenaming) {
            mainContent =
              '<span class="saved-colours-group-row__name saved-colours-group-row__name--editing">' +
              '<label class="visually-hidden" for="' + escapeHtml(renameInputId) + '">' + escapeHtml(t('renameGroup', { name: g.name })) + '</label>' +
              '<input type="text" class="saved-colours-group-row__rename-input" id="' + escapeHtml(renameInputId) + '" data-sc-rename-input value="' + escapeHtml(g.name) + '" maxlength="40"' + (renameError ? ' aria-invalid="true"' : '') + '>' +
              (renameError ? '<span class="saved-colours-group-row__rename-error" data-sc-rename-error role="alert">' + escapeHtml(renameError) + '</span>' : '') +
              '</span>';
          } else if (isDeleting) {
            mainContent =
              '<span class="saved-colours-group-row__name saved-colours-group-row__confirm-text">' +
              t('deleteGroupConfirm', { name: '<strong>' + escapeHtml(g.name) + '</strong>' }) +
              '</span>';
          } else {
            mainContent =
              '<button type="button" class="saved-colours-group-row__select" data-sc-select-group aria-pressed="' + (isSelected ? 'true' : 'false') + '">' +
              '<span class="saved-colours-group-row__name">' + escapeHtml(g.name) + '</span>' +
              '</button>';
          }

          var actions;
          if (isRenaming) {
            actions =
              '<span class="saved-colours-group-row__actions">' +
              '<button type="button" class="saved-colours-group-row__action-btn saved-colours-group-row__action-btn--primary" data-sc-confirm-rename>Save</button>' +
              '<button type="button" class="saved-colours-group-row__action-btn" data-sc-cancel-rename>Cancel</button>' +
              '</span>';
          } else if (isDeleting) {
            actions =
              '<span class="saved-colours-group-row__actions">' +
              '<button type="button" class="saved-colours-group-row__action-btn saved-colours-group-row__action-btn--danger" data-sc-confirm-delete>' + escapeHtml(t('deleteGroupConfirmButton')) + '</button>' +
              '<button type="button" class="saved-colours-group-row__action-btn" data-sc-cancel-delete>' + escapeHtml(t('keepGroup')) + '</button>' +
              '</span>';
          } else {
            var customIndex = customGroups.indexOf(g);
            var isFirst = customIndex === 0;
            var isLast = customIndex === customGroups.length - 1;
            actions =
              '<span class="saved-colours-group-row__actions">' +
              '<button type="button" class="saved-colours-group-row__icon-btn saved-colours-group-row__icon-btn--move" data-sc-move-group="up" aria-label="' + escapeHtml(t('moveGroupUp', { name: g.name })) + '"' + (isFirst ? ' disabled' : '') + '>' + UP_ICON + '</button>' +
              '<button type="button" class="saved-colours-group-row__icon-btn saved-colours-group-row__icon-btn--move" data-sc-move-group="down" aria-label="' + escapeHtml(t('moveGroupDown', { name: g.name })) + '"' + (isLast ? ' disabled' : '') + '>' + DOWN_ICON + '</button>' +
              '<button type="button" class="saved-colours-group-row__icon-btn" data-sc-rename-group aria-label="' + escapeHtml(t('renameGroup', { name: g.name })) + '">' + PENCIL_ICON + '</button>' +
              '<button type="button" class="saved-colours-group-row__icon-btn" data-sc-delete-group aria-label="' + escapeHtml(t('deleteGroup', { name: g.name })) + '">' + TRASH_ICON + '</button>' +
              '</span>';
          }

          return (
            '<li class="saved-colours-group-row' + (isRenaming || isDeleting ? ' is-active' : '') + (isSelected ? ' is-selected' : '') + '" data-group-id="' + escapeHtml(g.id) + '">' +
            mainContent +
            actions +
            '</li>'
          );
        }.bind(this)).join('');
      }

      handleAddGroup(event) {
        event.preventDefault();
        var input = this.addGroupForm.querySelector('input[name="group-name"]');
        if (!input) return;
        // An empty/whitespace-only submit is rejected before ever calling
        // Storage.addGroup() (which would also reject it) so the inline
        // error/red-border feedback fires for this case too, not only for
        // a duplicate name.
        if (!input.value.trim()) {
          this.showAddGroupError('empty-name', input);
          return;
        }
        var result = Storage.addGroup(input.value);
        if (result.ok) {
          input.value = '';
          this.clearAddGroupError();
        } else {
          this.showAddGroupError(result.error, input);
        }
      }

      // Visible, not just screen-reader-only: a rejected "add group"
      // submit leaves the form sitting there with no other feedback, so a
      // sighted user needs to actually see why nothing happened. This
      // element carries its own role="alert" in the markup, so it
      // announces itself — deliberately not also routed through the
      // shared getLiveRegion(), which would announce the same message
      // twice.
      showAddGroupError(errorCode, input) {
        if (!this.addGroupError) return;
        this.addGroupError.textContent = t(errorCode === 'duplicate-name' ? 'groupDuplicateName' : 'groupEmptyName');
        this.addGroupError.hidden = false;
        if (input) input.setAttribute('aria-invalid', 'true');
      }

      clearAddGroupError() {
        if (!this.addGroupError || this.addGroupError.hidden) return;
        this.addGroupError.hidden = true;
        this.addGroupError.textContent = '';
        var input = this.addGroupForm && this.addGroupForm.querySelector('input[name="group-name"]');
        if (input) input.removeAttribute('aria-invalid');
      }

      // Switching group/sort while scrolled deep into a long grid would
      // otherwise leave the customer looking at whatever happened to be
      // under their cursor from the *previous* filter — jump back to the
      // start of the results so the new filter/sort is what they land on.
      // Targets .saved-colours-main rather than the grid itself: the grid
      // is a child of the sticky toolbar's own row, so scrollIntoView on
      // it (or the toolbar) reasons about an already "stuck" element and
      // may treat it as already in view; .saved-colours-main isn't sticky
      // and its position doesn't depend on the grid ever having any
      // items, so it scrolls correctly even when the current filter has
      // zero results (the empty-state message takes the grid's place).
      scrollGridIntoView() {
        var target = this.querySelector('.saved-colours-main') || this.grid;
        if (!target) return;
        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }

      handleGridClick(event) {
        var removeBtn = event.target.closest('[data-sc-remove]');
        if (removeBtn) {
          var tile = removeBtn.closest('[data-sc-tile]');
          var productId = tile.dataset.productId;
          var colour = Storage.getColour(productId);
          // "All Saved Colours" isn't a real choice a customer made, so it
          // doesn't count here — only groups they explicitly added the
          // colour to.
          var customGroupCount = colour
            ? colour.groupIds.filter(function (id) { return id !== Storage.DEFAULT_GROUP_ID; }).length
            : 0;
          // Viewing "All Saved Colours" (activeGroup 'all') has no single
          // "current" group to exclude — removing here always means every
          // group at once, so belonging to even one custom group is enough
          // to warn. Viewing one specific group, that group itself doesn't
          // count as "another" one — only a *second* custom group makes
          // this removal affect more than the group already on screen.
          var isMasterView = this.activeGroup === 'all';
          var needsConfirm = isMasterView ? customGroupCount >= 1 : customGroupCount > 1;
          if (needsConfirm) {
            this.confirmingRemoveProductId = productId;
            this.render();
          } else {
            Storage.removeColour(productId);
            getLiveRegion().textContent = t('colourRemoved');
          }
          return;
        }

        var confirmRemoveBtn = event.target.closest('[data-sc-confirm-remove]');
        if (confirmRemoveBtn) {
          var confirmTile = confirmRemoveBtn.closest('[data-sc-tile]');
          this.confirmingRemoveProductId = null;
          Storage.removeColour(confirmTile.dataset.productId);
          getLiveRegion().textContent = t('colourRemoved');
          return;
        }

        var cancelRemoveBtn = event.target.closest('[data-sc-cancel-remove]');
        if (cancelRemoveBtn) {
          this.confirmingRemoveProductId = null;
          this.render();
          return;
        }

        var editGroupsBtn = event.target.closest('[data-sc-edit-groups]');
        if (editGroupsBtn) {
          var editTile = editGroupsBtn.closest('[data-sc-tile]');
          var productId = editTile.dataset.productId;
          this.openGroupEditorProductId = this.openGroupEditorProductId === productId ? null : productId;
          this.render();
        }
      }

      // Returns the currently-rendered <li> for a group id, or null.
      getGroupRow(groupId) {
        return this.groupManager.querySelector('.saved-colours-group-row[data-group-id="' + groupId + '"]');
      }

      focusGroupControl(groupId, selector) {
        var row = this.getGroupRow(groupId);
        var control = row && row.querySelector(selector);
        if (control) control.focus();
      }

      handleGroupManagerClick(event) {
        var row = event.target.closest('.saved-colours-group-row');
        if (!row) return;
        var groupId = row.dataset.groupId;

        if (event.target.closest('[data-sc-select-group]')) {
          // The default row filters down to "all" rather than its own
          // group id — "All Saved Colours" is the unfiltered master list,
          // so selecting it should show every saved colour, including any
          // whose data predates this group being locked/always-on.
          this.activeGroup = row.classList.contains('saved-colours-group-row--default') ? 'all' : groupId;
          // See the groupFilter 'change' handler above — a pending remove
          // confirmation shouldn't survive a switch to a different group.
          this.confirmingRemoveProductId = null;
          this.render();
          this.scrollGridIntoView();
          return;
        }

        var moveBtn = event.target.closest('[data-sc-move-group]');
        if (moveBtn) {
          var state = Storage.getState();
          var customGroups = state.groups.filter(function (g) { return !g.isDefault; });
          var index = customGroups.findIndex(function (g) { return g.id === groupId; });
          var direction = moveBtn.dataset.scMoveGroup === 'up' ? -1 : 1;
          var swapIndex = index + direction;
          if (index === -1 || swapIndex < 0 || swapIndex >= customGroups.length) return;

          var tmp = customGroups[index];
          customGroups[index] = customGroups[swapIndex];
          customGroups[swapIndex] = tmp;

          var defaultGroup = state.groups.find(function (g) { return g.isDefault; });
          var orderedIds = (defaultGroup ? [defaultGroup] : []).concat(customGroups).map(function (g) { return g.id; });
          Storage.reorderGroups(orderedIds);
          // Refocus a control that's always present and enabled on the
          // moved row — the very button just clicked may now be at the
          // start/end of the list and disabled, unable to hold focus.
          this.focusGroupControl(groupId, '[data-sc-rename-group]');
          return;
        }

        if (event.target.closest('[data-sc-rename-group]')) {
          this.renamingGroupId = groupId;
          this.deletingGroupId = null;
          this.renameGroupError = null;
          this.renderGroupManager(Storage.getState());
          var input = this.getGroupRow(groupId).querySelector('[data-sc-rename-input]');
          if (input) {
            input.focus();
            input.select();
          }
          return;
        }

        if (event.target.closest('[data-sc-cancel-rename]')) {
          this.renamingGroupId = null;
          this.renameGroupError = null;
          this.renderGroupManager(Storage.getState());
          this.focusGroupControl(groupId, '[data-sc-rename-group]');
          return;
        }

        if (event.target.closest('[data-sc-confirm-rename]')) {
          var renameInput = row.querySelector('[data-sc-rename-input]');
          var result = Storage.renameGroup(groupId, renameInput.value);
          if (!result.ok) {
            // Stay in rename mode and show the error inline right under
            // this row's input, rather than only announcing it to screen
            // readers — a sighted user needs to see why "Save" appeared
            // to do nothing.
            this.renameGroupError = {
              groupId: groupId,
              message: t(result.error === 'duplicate-name' ? 'groupDuplicateName' : 'groupEmptyName'),
            };
            this.renderGroupManager(Storage.getState());
            var retryInput = this.getGroupRow(groupId).querySelector('[data-sc-rename-input]');
            if (retryInput) {
              retryInput.focus();
              retryInput.select();
            }
            return;
          }
          this.renameGroupError = null;
          this.renamingGroupId = null;
          this.renderGroupManager(Storage.getState());
          this.focusGroupControl(groupId, '[data-sc-rename-group]');
          return;
        }

        if (event.target.closest('[data-sc-delete-group]')) {
          this.deletingGroupId = groupId;
          this.renamingGroupId = null;
          this.renderGroupManager(Storage.getState());
          this.focusGroupControl(groupId, '[data-sc-confirm-delete]');
          return;
        }

        if (event.target.closest('[data-sc-cancel-delete]')) {
          this.deletingGroupId = null;
          this.renderGroupManager(Storage.getState());
          this.focusGroupControl(groupId, '[data-sc-delete-group]');
          return;
        }

        if (event.target.closest('[data-sc-confirm-delete]')) {
          Storage.deleteGroup(groupId);
          getLiveRegion().textContent = t('groupDeleted');
          if (this.activeGroup === groupId) {
            this.activeGroup = 'all';
          }
          this.deletingGroupId = null;
          this.render();
        }
      }

      handleGroupManagerKeydown(event) {
        if (event.key === 'Enter' && event.target.matches('[data-sc-rename-input]')) {
          event.preventDefault();
          var row = event.target.closest('.saved-colours-group-row');
          var confirmBtn = row && row.querySelector('[data-sc-confirm-rename]');
          if (confirmBtn) confirmBtn.click();
          return;
        }

        if (event.key === 'Escape' && (this.renamingGroupId || this.deletingGroupId)) {
          var groupId = this.renamingGroupId || this.deletingGroupId;
          var wasRenaming = !!this.renamingGroupId;
          this.renamingGroupId = null;
          this.deletingGroupId = null;
          this.renameGroupError = null;
          this.renderGroupManager(Storage.getState());
          this.focusGroupControl(groupId, wasRenaming ? '[data-sc-rename-group]' : '[data-sc-delete-group]');
        }
      }

      // Clears a rename's error the moment the user starts editing again,
      // rather than leaving stale "already exists" text sitting under an
      // input they're actively changing.
      handleGroupManagerInput(event) {
        if (!this.renameGroupError) return;
        if (!event.target.matches('[data-sc-rename-input]')) return;
        var row = event.target.closest('.saved-colours-group-row');
        if (!row || row.dataset.groupId !== this.renameGroupError.groupId) return;
        this.renameGroupError = null;
        var errorEl = row.querySelector('[data-sc-rename-error]');
        if (errorEl) errorEl.remove();
      }
    }

    customElements.define('saved-colours-page', SavedColoursPageElement);
  }

  // ---------------------------------------------------------------------
  // Availability check (progressive, viewport-driven, never blocks render)
  // ---------------------------------------------------------------------
  var availabilityCache = new Map();
  function checkAvailability(link) {
    var href = link.getAttribute('href');
    if (!href || availabilityCache.has(href)) {
      applyAvailability(link, availabilityCache.get(href));
      return;
    }

    fetch(href + '.js', { method: 'GET' })
      .then(function (response) {
        availabilityCache.set(href, response.ok);
        applyAvailability(link, response.ok);
      })
      .catch(function () {
        // Network/blocked: assume available rather than falsely flagging it.
        availabilityCache.set(href, true);
      });
  }

  function applyAvailability(link, available) {
    if (available === false) {
      var tile = link.closest('[data-sc-tile]');
      link.setAttribute('aria-disabled', 'true');
      var msg = tile && tile.querySelector('[data-sc-unavailable]');
      if (msg) msg.hidden = false;
    }
  }

  var availabilityObserver = 'IntersectionObserver' in global
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            checkAvailability(entry.target);
            availabilityObserver.unobserve(entry.target);
          }
        });
      })
    : null;

  function observeAvailabilityLinks(root) {
    if (!availabilityObserver) return;
    (root || document).querySelectorAll('[data-sc-availability-link]').forEach(function (link) {
      availabilityObserver.observe(link);
    });
  }

  // ---------------------------------------------------------------------
  // MutationObserver: pick up save buttons injected by infinite scroll /
  // AJAX facet reloads that land before this script's custom elements have
  // had a chance to upgrade, and re-scan the saved-colours page grid.
  // ---------------------------------------------------------------------
  function observeDynamicContent() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;

          if (node.matches && node.matches('colour-save-button') && !node.dataset.scReady) {
            node.connectedCallback && node.connectedCallback();
          }
          node.querySelectorAll && node.querySelectorAll('colour-save-button:not([data-sc-ready])').forEach(function (el) {
            el.connectedCallback && el.connectedCallback();
          });

          if (node.querySelectorAll) {
            observeAvailabilityLinks(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
  }

  function init() {
    defineSaveButton();
    defineToast();
    defineSavedColoursPage();
    observeDynamicContent();
    observeAvailabilityLinks(document);
    initInfoTooltips();

    if (global.SavedColoursSync) {
      global.SavedColoursSync.init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window);
